"use client";

import { useEffect, useRef, useState } from "react";
import { Pause, Play, Terminal } from "lucide-react";
import { TwinMark } from "@product/components/app/twin/twin-mark";
import { TwinChatCard } from "@/components/product-shot/twin-chat-card";
import { AssistantMark, ExternalChat } from "./external-chat";
import { NORTHSTAR_ANSWER, answerSources, answerText, type StoryAnswer } from "./answer-content";
import { DOORS, type Door } from "./doors";
import styles from "./connected-answer.module.css";

/** A door's name on its tab: its card's name, unless it has a shorter one for the tab. */
const tabName = (door: Door) => ("tab" in door ? door.tab : door.name);

function DoorIcon({ door }: { door: Door }) {
  if (door.id === "twin") return <TwinMark />;
  if (door.id === "terminal") return <Terminal size={18} aria-hidden="true" />;
  return <AssistantMark provider={door.id} />;
}

function TerminalAnswer({ answer }: { answer: StoryAnswer }) {
  return <div className={styles.terminal}>
    <div className={styles.terminalDots} aria-hidden="true"><i /><i /><i /><span>twin, business context</span></div>
    <pre><code><span className={styles.shellPrompt}>$ </span>{`twin query --question ${JSON.stringify(answer.question)}`}</code></pre>
    <p className={styles.terminalLabel}>ANSWER</p>
    <p>{answer.parts.map(part => part.text).join("")}</p>
    <div className={styles.terminalRecords}><span>SOURCES</span><p>{answer.records.map((record, index) => <span key={record.name}>0{index + 1}  {record.system.split(" / ")[0]} / {record.title}<br /></span>)}</p></div>
    <p className={styles.terminalNote}>The same context, from your terminal.</p>
    <span className={styles.shellPrompt} aria-hidden="true">$ ▍</span>
  </div>;
}

/** How long each client stays in front before the stack shuffles. */
const SHUFFLE_MS = 2000;

/**
 * One answer across six clients; only the front card is interactive.
 * `dense` gives the stack the shorter height it takes on a short screen,
 * for a place that sets it beside other art (the home's row of steps).
 *
 * Twin chat is the application itself: one finished turn in the product's
 * own components (product-shot/twin-chat-card), as the hero's shot is. The
 * other five derive from it: the same question, sentence and systems, in
 * each client's own look, so a reader can tell them apart (Archie,
 * 2026-10-01).
 *
 * The five that picture someone else's product keep the corners they were
 * drawn with (`data-own-corners`): a rounded window, bubbles, composer and
 * send button are part of what makes ChatGPT, Claude, Gemini, Copilot and
 * a terminal recognisable, so a page that squares its corners (one-system)
 * leaves them alone. Twin chat is ours: its card keeps the slight radius
 * every product shot's window has (`data-frame-corners`, on the card and
 * on the pane that fills its foot), and what it holds is square.
 */
export function ConnectedAnswer({ active, answer = NORTHSTAR_ANSWER, dense = false }: { active: boolean; answer?: StoryAnswer; dense?: boolean }) {
  const root = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [focused, setFocused] = useState(false);
  const [visible, setVisible] = useState(false);
  const [reduced, setReduced] = useState(true);
  const [keyboard, setKeyboard] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotion = () => setReduced(media.matches);
    updateMotion();
    media.addEventListener("change", updateMotion);
    let intersecting = false;
    const updateVisibility = () => setVisible(intersecting && !document.hidden);
    const observer = new IntersectionObserver(([entry]) => { intersecting = !!entry?.isIntersecting; updateVisibility(); });
    if (root.current) observer.observe(root.current);
    document.addEventListener("visibilitychange", updateVisibility);
    return () => { observer.disconnect(); media.removeEventListener("change", updateMotion); document.removeEventListener("visibilitychange", updateVisibility); };
  }, []);

  useEffect(() => {
    if (!active || !visible || !playing || focused || reduced) return;
    const timer = window.setTimeout(() => { setKeyboard(false); setSelected(index => (index + 1) % DOORS.length); }, SHUFFLE_MS);
    return () => window.clearTimeout(timer);
  }, [active, visible, playing, focused, reduced, selected]);

  return <div ref={root} className={styles.exhibit} data-keyboard={keyboard} data-dense={dense}>
    <div className={styles.stack}
      onFocusCapture={() => setFocused(true)} onBlurCapture={event => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}>
      {DOORS.map((door, index) => <article key={door.id} className={styles.card} data-door={door.id} data-own-corners={door.id === "twin" ? undefined : ""} data-frame-corners={door.id === "twin" ? "" : undefined} data-depth={(index - selected + DOORS.length) % DOORS.length} aria-hidden={selected !== index} inert={selected !== index} aria-label={`${door.name} illustrative interface`}>
        <header><DoorIcon door={door} /><strong>{door.name}</strong><span>{door.connection}</span></header>
        <div className={styles.cardContent} data-frame-corners={door.id === "twin" ? "" : undefined}>
          {door.id === "twin" ? <TwinChatCard question={answer.question} answerMarkdown={answerText(answer)} sources={answerSources(answer)} /> : door.id === "terminal" ? <TerminalAnswer answer={answer} /> : <ExternalChat provider={door.id} answer={answer} dense={dense} />}
        </div>
      </article>)}
    </div>
    <div className={styles.controls} aria-label="Choose how to use the Twin">
      {DOORS.map((door, index) => <button key={door.id} data-door={door.id} type="button" aria-label={`Show ${door.name}`} aria-pressed={selected === index} onClick={event => { setKeyboard(event.detail === 0); setSelected(index); }}><DoorIcon door={door} /><span>{tabName(door)}</span></button>)}
      {!reduced && <button type="button" className={styles.play} aria-label={playing ? "Pause card shuffle" : "Resume card shuffle"} onClick={() => setPlaying(value => !value)}>{playing ? <Pause size={13} aria-hidden="true" /> : <Play size={13} aria-hidden="true" />}</button>}
    </div>
    {/* Each card says how its tool connects (MCP, the Twin agent, the CLI), so the note does not name one route for all. */}
    <p className={styles.caption}>One Twin. Your tools. The same business knowledge.<span>Example interfaces · External tools connect with your permission</span></p>
  </div>;
}
